import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";

const SLIDES = [
  {
    id: "dash",
    titleEn: "DASHBOARD",
    titleKo: "대시보드",
    desc: "중대재해 발생 가능 현장을 실시간으로 모니터링하고, 이해관계자 현황과 예방대책 이행 상태를 한눈에 파악하세요.",
    features: [
      { title: "중대재해 발생 가능 현황", sub: "위험도 높은 현장을 실시간 파악" },
      { title: "이해관계자 현황", sub: "안전 관리 책임자·감독자 확인" },
      { title: "예방대책 이행 현황", sub: "현장별 예방 조치 점검" },
    ],
    path: "/dash/current",
    img: "/img/slide_dashboard.png",
    gradient: "from-slate-950/90 via-blue-950/70 to-transparent",
    accent: "#60A5FA",
    tag: null,
  },
  {
    id: "acci",
    titleEn: "INCIDENT MANAGEMENT",
    titleKo: "재해관리",
    desc: "중대재해 예방을 위해 공정·설비별 위험요인을 기록하고, 관리감독자와 이해관계자의 역할·서명·이력을 한 곳에서 체계적으로 관리하세요.",
    features: [
      { title: "재해 발생 보고 및 관리", sub: "발생 즉시 등록하고 조치 현황을 추적" },
      { title: "공정·설비별 위험요인 관리", sub: "위험요인 기록과 개선 조치 이력 관리" },
      { title: "이해관계자 커뮤니케이션", sub: "현장 관계자와 협업·공지·이력 관리" },
    ],
    path: "/acci/ProcEqpt",
    img: "/img/slide_accident.png",
    gradient: "from-slate-950/90 via-orange-950/70 to-transparent",
    accent: "#FB923C",
    tag: null,
  },
  {
    id: "comp",
    titleEn: "WORKPLACE MANAGEMENT",
    titleKo: "사업장관리",
    desc: "사업장, 부서, 직위, 사원 정보를 하나의 플랫폼에서 체계적으로 관리하고 조직 계층을 명확히 구성하세요.",
    features: [
      { title: "사업장 목록 관리", sub: "전체 사업장 현황 및 정보 관리" },
      { title: "부서·직위·사원 관리", sub: "조직 계층 체계적 관리" },
      { title: "안전 인력 배치", sub: "현장별 담당자 지정 및 관리" },
    ],
    path: "/comp/SiteList",
    img: "/img/slide_workplace.png",
    gradient: "from-slate-950/90 via-emerald-950/70 to-transparent",
    accent: "#34D399",
    tag: null,
  },
  {
    id: "corpreg",
    titleEn: "COMPANY MANAGEMENT",
    titleKo: "고객사 관리",
    desc: "법인 정보와 사업장을 등록하고, 전사 조직 구조를 기반으로 안전관리 체계를 설정하세요.",
    features: [
      { title: "회사 정보 관리", sub: "법인 기본 정보 및 등록" },
      { title: "사업장 등록", sub: "회사 내 사업장 추가 및 관리" },
      { title: "조직 구조 설정", sub: "전사 조직도 기반 체계 구성" },
    ],
    path: "/comp/corpreg",
    img: "/img/slide_company.png",
    gradient: "from-slate-950/90 via-indigo-950/70 to-transparent",
    accent: "#818CF8",
    tag: null,
  },
  {
    id: "biz",
    titleEn: "BUSINESS CARD MANAGEMENT",
    titleKo: "명함관리",
    desc: "종이 명함을 찍으면 AI가 자동으로 인식·변환합니다. 그룹 관리와 공유 링크로 인맥을 스마트하게 관리하세요.",
    features: [
      { title: "AI OCR 자동 인식", sub: "사진 한 장으로 즉시 디지털화" },
      { title: "그룹별 명함 관리", sub: "팀·프로젝트 단위로 분류" },
      { title: "공유 링크 생성", sub: "링크 하나로 간편 공유" },
    ],
    path: "/guide/biz",
    img: null,
    gradient: "from-slate-950/90 via-cyan-950/70 to-transparent",
    accent: "#22D3EE",
    tag: "NEW",
  },
];

const FeatureCarousel = () => {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(null);
  const [animating, setAnimating] = useState(false);
  const [progress, setProgress] = useState(0);
  const autoMs = 5000;
  const rafRef = useRef(null);
  const startTsRef = useRef(0);
  const animatingRef = useRef(false);

  const go = useCallback((next, dir) => {
    if (animatingRef.current) return;
    animatingRef.current = true;
    setDirection(dir);
    setAnimating(true);
    setCurrent(next);
    setTimeout(() => {
      setAnimating(false);
      animatingRef.current = false;
    }, 400);
  }, []);

  const prev = () => go((current - 1 + SLIDES.length) % SLIDES.length, "left");
  const next = useCallback(() => go((current + 1) % SLIDES.length, "right"), [current, go]);

  // 슬라이드 변경 시 진행 바 리셋
  useEffect(() => {
    startTsRef.current = performance.now();
    setProgress(0);
  }, [current]);

  // 진행 바 애니메이션 (rAF)
  useEffect(() => {
    if (SLIDES.length <= 1) return;

    const tick = (ts) => {
      const elapsed = ts - startTsRef.current;
      const p = Math.min(1, elapsed / autoMs);
      setProgress(p);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [current]);

  // 자동 전환 (게이지 완료 후 즉시)
  useEffect(() => {
    if (SLIDES.length <= 1) return;
    const timer = setTimeout(() => {
      go((current + 1) % SLIDES.length, "right");
    }, autoMs);
    return () => clearTimeout(timer);
  }, [current, go]);

  const slide = SLIDES[current];

  const enterStyle = {
    animation: animating
      ? `slideIn${direction === "right" ? "Right" : "Left"} 0.4s ease forwards`
      : "none",
  };

  return (
    <div className="mt-4 w-full relative">
      {/* 캐러셀 */}
      <div className="relative overflow-hidden rounded-3xl shadow-xl" style={{ height: slide.features ? "600px" : "520px", transition: "height 0.4s ease" }}>
        {/* 배경 이미지 */}
        <div
          key={current}
          className="absolute inset-0 bg-center bg-cover transition-all duration-500"
          style={slide.img ? { backgroundImage: `url(${slide.img})` } : { background: "linear-gradient(135deg, #0f172a, #1e293b)" }}
        />

        {/* 그라데이션 오버레이 */}
        <div className={`absolute inset-0 bg-gradient-to-r ${slide.gradient}`} />

        {/* 컨텐츠 */}
        <div
          key={`content-${current}`}
          className="absolute inset-0 flex flex-col justify-center px-8 sm:px-12 pb-10 sm:pb-12 pt-10 sm:pt-12"
          style={enterStyle}
        >
          {/* 태그 */}
          {slide.tag && (
            <div
              className="mb-3 inline-flex w-fit items-center rounded-full px-3 py-1 text-[13px] font-extrabold uppercase tracking-widest"
              style={{ background: `${slide.accent}22`, color: slide.accent, border: `1px solid ${slide.accent}44` }}
            >
              {slide.tag}
            </div>
          )}

          {/* 영문 */}
          <p className="text-[16px] sm:text-[18px] font-bold tracking-[0.2em] text-white/50 uppercase">
            {slide.titleEn}
          </p>

          {/* 한글 제목 */}
          <h3 className="mt-2 text-[52px] sm:text-[64px] md:text-[76px] font-black text-white leading-none">
            {slide.titleKo}
          </h3>

          {/* 설명 */}
          <p className="mt-5 text-[20px] sm:text-[22px] font-semibold text-white/70 max-w-[680px] leading-relaxed">
            {slide.desc}
          </p>

          {/* 서브 기능 목록 + 바로가기 버튼 */}
          <div className="mt-5 flex items-end justify-between gap-6">
            {slide.features ? (
              <ul className="space-y-2">
                {slide.features.map((f) => (
                  <li key={f.title} className="text-[20px] sm:text-[22px] font-bold text-white/80 leading-relaxed">
                    {f.title}
                  </li>
                ))}
              </ul>
            ) : <span />}
            <button
              onClick={() => navigate(slide.path)}
              className="shrink-0 inline-flex items-center rounded-xl px-8 py-4 text-[17px] sm:text-[19px] font-extrabold text-white transition-all duration-200 hover:brightness-110 hover:scale-105"
              style={{ background: slide.accent }}
            >
              바로가기 →
            </button>
          </div>
        </div>

        {/* 슬라이드 인디케이터 — 캐러셀 내부 하단 중앙 */}
        {SLIDES.length > 1 && (
          <div className="absolute bottom-4 left-1/2 z-20 -translate-x-1/2">
            <div className="flex items-center gap-1.5 rounded-full bg-black/30 px-2 py-1 backdrop-blur-sm">
              {SLIDES.map((s, i) => {
                const isActive = i === current;
                return (
                  <button
                    key={s.id}
                    type="button"
                    disabled={animating}
                    onClick={() => go(i, i > current ? "right" : "left")}
                    className="relative overflow-hidden rounded-full transition-all duration-300 disabled:opacity-60"
                    style={{
                      width: isActive ? "28px" : "6px",
                      height: "6px",
                      background: "rgba(255,255,255,0.25)",
                    }}
                  >
                    {isActive && (
                      <span
                        className="absolute inset-y-0 left-0 rounded-full"
                        style={{
                          width: `${progress * 100}%`,
                          background: slide.accent,
                          transition: "none",
                        }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(40px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-40px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

export default FeatureCarousel;
